/**
 * legal-document — renders one finished `LegalDocument` (title, version,
 * sections of paragraph/list blocks) inside whatever overlay mounts it, or
 * embedded directly in a page. Content-only per schmancy's overlay
 * contract: no full-bleed surface, no padding from the host — this
 * component supplies its own.
 *
 * Two geometries, one component (`embedded`). Standing alone in an overlay,
 * the document is its own card: bounded at `max-h-[80vh]`, its body an
 * `overflowWithin()` scroll region. Embedded in a surface that already owns
 * a scroll region — the public `/legal` page, the admin editor's review
 * panes — that same card is a trap: `overflowWithin()` sets
 * `overscroll-behavior: contain`, so once the reader's wheel reaches the end
 * of the document's own body it never chains out to the host, and everything
 * below the document (the next section, the page's own footer, the editor's
 * publish bar) is unreachable while the cursor sits over it. Embedded, the
 * document drops the cap and the inner scroller and grows to its full
 * height; the host scrolls it.
 *
 * A mounting surface with something to append to one clause — the privacy
 * policy's own opt-out switch, which §3 promises at the end of §3 — passes
 * that clause's heading as `appendAfterHeading` and slots the control as a
 * light-DOM child. The document places it and knows nothing else about it,
 * so what is appended stays the mounting surface's own concern.
 *
 * The version line is text everywhere a reader meets it, and a correctable
 * field only where `editableVersion` says so (the admin editor's review
 * panes). It still writes nothing — the correction leaves as a
 * `version-change` event and whoever owns the draft decides what to do with
 * it, so this component stays a renderer of a `LegalDocument` value rather
 * than an editor of one.
 */
import { overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import type { LegalDocument } from "@momo/i18n";
import { html } from "lit";
import { property } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { LocalizeMixin } from "../i18n/localize.ts";

@schmancyElement("legal-document")
export class LegalDocumentView extends LocalizeMixin(SchmancyElement) {
	/** `null` when the organization's own text could not be loaded: the card
	 *  then says so in place of a title, and no text stands in for it. */
	@property({ type: Object }) document: LegalDocument | null = null;
	/** Set when the operator's workspace card hadn't resolved at build time —
	 *  every `LegalIdentity` field in `document` is then the catalog's
	 *  `identityUnavailable` placeholder rather than a real value. */
	@property({ type: Boolean }) identityUnavailable = false;
	/** Whether this document offers its own close affordance. True by
	 *  default — every existing `show()` overlay call site keeps working
	 *  with no change. A mounting site with nothing to close (a page, a
	 *  preview pane) sets this false so the close button never renders as a
	 *  control that looks interactive and does nothing. */
	@property({ type: Boolean }) dismissible = true;
	/** Whether the mounting surface already owns a scroll region this document
	 *  should flow inside. False by default — every existing `show()` overlay
	 *  call site keeps its own bounded card, pixel-identical. A page or a
	 *  review pane sets it true so the document grows to its full height and
	 *  the host scrolls it; see this file's own header for why the card's
	 *  inner scroller is a trap inside one. */
	@property({ type: Boolean }) embedded = false;
	/** The heading of the section this document's own light-DOM children
	 *  follow — a mounting surface appending one control to one clause,
	 *  addressed by the clause rather than by its position, since a document
	 *  gains and loses sections without renumbering what it says. Null by
	 *  default, and null is also what an unmatched heading resolves to: the
	 *  children then follow the last section, so a document whose text was
	 *  rewritten by an operator still carries whatever was appended to it.
	 *  Every existing mount passes no children at all, so the slot renders
	 *  nothing and the document is pixel-identical. */
	@property({ type: String }) appendAfterHeading: string | null = null;
	/** Whether the version line accepts a correction in place, dispatching
	 *  `version-change` rather than writing anything itself. False by default,
	 *  so every reader-facing mount — the public page, the checkout and consent
	 *  overlays — renders the version as text and carries no mutation surface
	 *  at all. The editor's review panes set it true because a document states
	 *  its own version, and the one place an operator can judge whether the
	 *  stated version is right is beside the document that states it. */
	@property({ type: Boolean }) editableVersion = false;

	override render() {
		const doc = this.document;
		const docSections = doc?.sections ?? [];
		const appendAfter = docSections.some(
			(section) => section.heading === this.appendAfterHeading,
		)
			? this.appendAfterHeading
			: (docSections[docSections.length - 1]?.heading ?? null);
		const sections = repeat(
			docSections,
			(section) => section.heading,
			(section) => html`
				<div class="flex flex-col gap-2">
					<schmancy-typography type="title" token="sm">${section.heading}</schmancy-typography>
					${repeat(
						section.blocks,
						(_block, i) => i,
						(block) =>
							block.kind === "p"
								? html`<schmancy-typography type="body" token="md"
										>${block.text}</schmancy-typography
									>`
								: html`
										<ul class="flex flex-col gap-1 list-disc pl-6">
											${repeat(
												block.items,
												(item) => item,
												(item) =>
													html`<li>
														<schmancy-typography type="body" token="md"
															>${item}</schmancy-typography
														>
													</li>`,
											)}
										</ul>
									`,
					)}
					${when(section.heading === appendAfter, () => html`<slot></slot>`)}
				</div>
			`,
		);
		return html`
			<div
				class=${this.classMap({
					"grid w-full max-w-2xl p-6 gap-4": true,
					"grid-rows-[auto_1fr] max-h-[80vh]": !this.embedded,
				})}
			>
				<div class="flex flex-col gap-3">
					<div class="flex items-start justify-between gap-4">
						${when(
							doc,
							(doc) => html`
								<div class="flex flex-col gap-1">
									<schmancy-typography type="headline" token="sm">${doc.title}</schmancy-typography>
									${when(
										this.editableVersion,
										() => html`
											<schmancy-input
												class="w-44 max-w-full"
												size="sm"
												aria-label=${this.t("legal.viewer.versionLabel")}
												.value=${doc.version}
												@change=${(e: CustomEvent<{ value: string }>) => {
													// schmancy's value-controlled inputs re-emit `change` on the
													// consumer's own `.value=` binding, first render included —
													// an equal value is that echo, not a correction.
													if (e.detail.value === doc.version) return;
													this.dispatchEvent(
														new CustomEvent("version-change", {
															detail: { version: e.detail.value },
															bubbles: true,
															composed: true,
														}),
													);
												}}
											></schmancy-input>
										`,
										() => html`
											<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
												${this.t("legal.viewer.version", { version: doc.version })}
											</schmancy-typography>
										`,
									)}
								</div>
							`,
							() => html`
								<schmancy-typography role="alert" type="body" token="md" class="text-error-default">${this.t("legal.unavailableDocument")}</schmancy-typography>
							`,
						)}
						${when(
							this.dismissible,
							() => html`
								<schmancy-icon-button
									icon="close"
									variant="text"
									size="sm"
									aria-label=${this.t("legal.viewer.close")}
									@click=${() =>
										this.dispatchEvent(
											new CustomEvent("close", {
												bubbles: true,
												composed: true,
											}),
										)}
								></schmancy-icon-button>
							`,
						)}
					</div>

					${when(
						this.identityUnavailable,
						() => html`
							<schmancy-surface type="containerHigh" rounded="all" class="px-4 py-2 rounded-xl">
								<schmancy-typography type="body" token="sm">
									${this.t("legal.identityUnavailableNotice")}
								</schmancy-typography>
							</schmancy-surface>
						`,
					)}
				</div>

				${when(
					this.embedded,
					() => html`<div class="flex flex-col gap-6">${sections}</div>`,
					() =>
						html`<div ${overflowWithin()} class="flex flex-col gap-6">${sections}</div>`,
				)}
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"legal-document": LegalDocumentView;
	}
	interface HTMLElementEventMap {
		/** A correction to the version line, dispatched only by a mount that
		 *  opted into `editableVersion`. Whoever owns the draft decides what to
		 *  do with it; this document writes nothing. */
		"version-change": CustomEvent<{ version: string }>;
	}
}
